/**
 * Agent Function API Client
 * Handles all API calls for Agent Function entities
 */

import { BaseApiClient, ApiResponse } from './baseApiClient';
import type { StandardListRequest, GetByIdRequest } from '../types';

export interface AgentFunction {
  AgentFunctionID: number;
  Name: string;
  Description?: string;
  FunctionCode: string;
  IsActive: boolean;
  CreatedOn?: string;
}

export class AgentFunctionApiClient extends BaseApiClient {
  /**
   * Get all active agent functions
   */
  async getActive(request: StandardListRequest = {}): Promise<ApiResponse<AgentFunction[]>> {
    const requestWithPagination = {
      PageInfo: {
        PageNumber: 1,
        PageSize: 1000,
      },
      ...request,
    };
    return this.postQuery<AgentFunction[]>('/api/v1/ai-function/agent-functions/active', requestWithPagination);
  }

  /**
   * Get agent function by ID
   */
  async getById(request: GetByIdRequest): Promise<ApiResponse<AgentFunction>> {
    const webRequest = {
      ID: {
        ID: request.id,
      },
    };
    return this.postQuery<AgentFunction>(`/api/v1/ai-function/agent-functions/${request.id}`, webRequest);
  }

  /**
   * Create new agent function
   */
  async create(agentFunction: any): Promise<ApiResponse<AgentFunction>> {
    const response = await this.post<any>('/api/v1/ai-function/agent-functions', agentFunction);

    // Handle response structure - API returns id in response.id.id or response.data.Id.id
    if (response.success) {
      let agentFunctionId = null;

      // Try to extract ID from various possible locations
      const r = response as any;
      if (r.id?.id) {
        agentFunctionId = r.id.id;
      } else if (response.data?.id?.id) {
        agentFunctionId = response.data.id.id;
      } else if (response.data?.Id?.id) {
        agentFunctionId = response.data.Id.id;
      }

      if (agentFunctionId) {
        return {
          success: true,
          data: {
            AgentFunctionID: agentFunctionId,
            ...agentFunction,
            IsActive: agentFunction.IsActive ?? true,
          } as AgentFunction,
        };
      }
    }

    // If we couldn't extract the ID but still got success, return what we have
    if (response.success && !response.data?.AgentFunctionID) {
      return {
        success: true,
        data: {
          AgentFunctionID: 0, // Fallback - will be updated from actual data
          ...agentFunction,
          IsActive: agentFunction.IsActive ?? true,
        } as AgentFunction,
      };
    }

    return response;
  }

  /**
   * Update existing agent function
   */
  async update(id: number, func: any): Promise<ApiResponse<AgentFunction>> {
    const webRequest = {
      Data: {
        AgentFunctionID: id,
        ...func,
      },
    };
    return this.put<AgentFunction>(`/api/v1/ai-function/agent-functions/${id}`, webRequest);
  }

  /**
   * Delete agent function
   */
  async deleteById(id: number): Promise<ApiResponse<void>> {
    const webRequest = {
      ID: {
        ID: id,
      },
    };
    return this.delete<void>(`/api/v1/ai-function/agent-functions/${id}`, webRequest);
  }

  /**
   * Activate agent function
   */
  async activate(id: number): Promise<ApiResponse<void>> {
    const webRequest = {
      AgentFunctionID: {
        ID: id,
      },
    };
    return this.post<void>('/api/v1/ai-function/agent-functions/activate', webRequest);
  }

  /**
   * Deactivate agent function
   */
  async deactivate(id: number): Promise<ApiResponse<void>> {
    const webRequest = {
      AgentFunctionID: {
        ID: id,
      },
    };
    return this.post<void>('/api/v1/ai-function/agent-functions/deactivate', webRequest);
  }
}

export const agentFunctionApiClient = new AgentFunctionApiClient();
